import React from 'react';

import { Flex, Image, ScrollArea, Spoiler, Text, useMantineTheme } from '@mantine/core';
import { AudioPlayer } from 'components/SmartAssistantComponents/AudioPlayer';
import { MessageBubble, MessageBubbleProps } from 'components/SmartAssistantComponents/MessageBubble';
import { useTranslation } from 'react-i18next';
import { CONVERSATION_VIEW_MAW } from 'shared/constants/theme';
import { ChatAttachment, ChatAttachmentType } from 'shared/interfaces/ChatMessage';

type ChatAttachmentsBubbleProps = MessageBubbleProps;

export function ChatAttachmentsBubble({ ...messageBubbleProps }: ChatAttachmentsBubbleProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { message } = messageBubbleProps;
  const { smartAssistantVisualConfig } = messageBubbleProps;
  const messageContentColor = theme.other.getReadableColor(
    message?.fromUser ? smartAssistantVisualConfig.userMessageColor : smartAssistantVisualConfig.assistantMessageColor
  );

  const ImageAttachments = ({ attachments }: { attachments: ChatAttachment[] }) => {
    return (
      <ScrollArea maw={`calc(${CONVERSATION_VIEW_MAW} * 0.6)`}>
        <Flex direction="row" gap="xs">
          {attachments.map((attachment, index) => (
            <Image
              key={attachment._id + index}
              src={attachment.displayUrl}
              onClick={() => {
                window.open(attachment.displayUrl, '_blank');
              }}
              style={{ cursor: 'pointer' }}
              width={60}
              height={60}
              radius="md"
              fit="cover"
            />
          ))}
        </Flex>
      </ScrollArea>
    );
  };

  const AudioAttachment = ({ attachment }: { attachment: ChatAttachment }) => {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'min-content',
          width: 'fit-content'
        }}>
        <AudioPlayer w={250} audioUrl={attachment.displayUrl} color={messageContentColor} />
        {attachment.description && (
          <Spoiler
            mt="xs"
            styles={{
              control: {
                color: messageContentColor
              }
            }}
            showLabel={t('APP.ACTIONS.SHOW_TRANSCRIPT')}
            maxHeight={0}
            hideLabel={t('APP.ACTIONS.HIDE_TRANSCRIPT')}>
            <Text fs="italic" c={messageContentColor} mb="xs">
              &quot;{attachment.description}&quot;
            </Text>
          </Spoiler>
        )}
      </div>
    );
  };

  const imageAttachments = message?.attachments?.filter((a) => a.type === ChatAttachmentType.IMAGE) || [];
  const audioAttachments = message?.attachments?.filter((a) => a.type === ChatAttachmentType.AUDIO) || [];

  return (
    <>
      {imageAttachments?.length > 0 && (
        <MessageBubble
          {...messageBubbleProps}
          // We don't want to show any text in the bubble, so we set the text to an empty string
          message={{ text: '', fromUser: messageBubbleProps.message?.fromUser ?? false }}>
          <ImageAttachments attachments={imageAttachments} />
        </MessageBubble>
      )}
      {audioAttachments?.length > 0 &&
        audioAttachments.map((attachment) => (
          <MessageBubble
            key={attachment._id}
            {...messageBubbleProps}
            // We don't want to show any text in the bubble, so we set the text to an empty string
            message={{ text: '', fromUser: messageBubbleProps.message?.fromUser ?? false }}>
            <AudioAttachment attachment={attachment} />
          </MessageBubble>
        ))}
    </>
  );
}
